import { CSSProperties, FC, ReactNode } from 'react';
import { Button, Divider, Row, Space } from 'antd';
import { TbCheck, TbPlus } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';

export interface FilterRowRecommendation {
    actionLabel?: string;
    icon?: ReactNode;
    key: string;
    label: string;
    onClick: () => void;
    selected?: boolean;
}

interface FilterRowSurfaceProps {
    children: ReactNode;
    horizontalPadding?: CSSProperties['paddingInline'];
    style?: CSSProperties;
}

interface FilterRowEmptyStateProps {
    entryAction?: ReactNode;
    hint?: ReactNode;
    recommendations?: FilterRowRecommendation[];
}

const FilterRecommendationButton: FC<{ recommendation: FilterRowRecommendation }> = ({ recommendation }) => {
    const { token } = useAntDesignTheme();
    const selectedStyle = recommendation.selected
        ? {
              backgroundColor: token.colorPrimaryBg,
              borderColor: token.colorPrimaryBorder,
              color: token.colorPrimary,
          }
        : undefined;

    return (
        <Space.Compact size='small'>
            <Button
                aria-pressed={Boolean(recommendation.selected)}
                size='small'
                style={{ alignItems: 'center', display: 'inline-flex', gap: '0.375rem', ...selectedStyle }}
                type='dashed'
                onClick={recommendation.onClick}
            >
                {recommendation.icon}
                <small>{recommendation.label}</small>
            </Button>
            <Button
                aria-label={
                    recommendation.actionLabel ??
                    `${recommendation.selected ? 'Remove' : 'Apply'} ${recommendation.label}${
                        recommendation.selected ? '' : ' filter template'
                    }`
                }
                aria-pressed={Boolean(recommendation.selected)}
                size='small'
                style={{ alignItems: 'center', display: 'inline-flex', ...selectedStyle }}
                type='dashed'
                onClick={recommendation.onClick}
            >
                {recommendation.selected ? (
                    <TbCheck aria-hidden size='0.75rem' />
                ) : (
                    <TbPlus aria-hidden size='0.75rem' />
                )}
            </Button>
        </Space.Compact>
    );
};

const FilterRowEmptyState: FC<FilterRowEmptyStateProps> = ({
    entryAction,
    hint,
    recommendations = [],
}): JSX.Element => {
    const { token } = useAntDesignTheme();

    return (
        <div
            style={{
                alignItems: 'center',
                display: 'flex',
                minWidth: 0,
                overflowX: 'auto',
                whiteSpace: 'nowrap',
            }}
        >
            {hint ? <small style={{ color: token.colorTextTertiary }}>{hint}</small> : null}
            {hint && (entryAction || recommendations.length) ? <Divider type='vertical' /> : null}
            {entryAction}
            {entryAction && recommendations.length ? <Divider type='vertical' /> : null}
            {recommendations.length ? (
                <Space size={4}>
                    {recommendations.map((recommendation) => (
                        <FilterRecommendationButton key={recommendation.key} recommendation={recommendation} />
                    ))}
                </Space>
            ) : null}
        </div>
    );
};

const FilterRowSurface: FC<FilterRowSurfaceProps> = ({
    children,
    horizontalPadding = '1.5rem',
    style,
}): JSX.Element => {
    const { token } = useAntDesignTheme();

    return (
        <Row
            align='middle'
            style={{
                borderBottom: `1px solid ${token.colorBorder}`,
                boxSizing: 'border-box',
                minHeight: '2.5rem',
                padding: '0.5rem 0',
                paddingInline: horizontalPadding,
                ...style,
            }}
            wrap={false}
        >
            {children}
        </Row>
    );
};

export { FilterRecommendationButton, FilterRowEmptyState, FilterRowSurface };
